<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router/composables'
import MixGridBlock from '@components/mix-grid-block'
import GenresList from '@components/genre/genres-list'
import ArtistBlock from '@components/artist/artist-block'
import ShowGridBlock from './../show/show-grid-block'
import MixTableBlock from '@components/mix-table-block.v2'
import BestMatch from '@components/search/best-match'
import extractGenres from '@utils/extract-genres'

const MIXES_SHOWN = 6
const ARTISTS_SHOWN = 6
const SHOWS_SHOWN = 4

const props = defineProps({
    searchResults: {
        type: Object,
        default: () => {},
    },
    bestMatchType: {
        type: String,
        default: '',
    },
    bestMatchModel: {
        type: [Object, Array],
        default: undefined,
    },
})

const route = useRoute()

const keyword = computed(() => route.params.keyword)

const hasBestMatch = computed(() => !!props.bestMatchType)

const mixes = computed(() => props.searchResults.Mixes.slice(0, MIXES_SHOWN))

const artists = computed(() => props.searchResults.Artists.slice(0, ARTISTS_SHOWN))

const shows = computed(() => props.searchResults.Shows.slice(0, SHOWS_SHOWN))

const genres = computed(() => mixes.value && extractGenres(mixes.value))
</script>

<template>
    <div>
        <BaseSection
            v-if="hasBestMatch && bestMatchType !== 'TrackLists'"
            class="best-match"
        >
            <h5 class="title">Best Match</h5>
            <BestMatch
                :best-match-model="bestMatchModel"
                :best-match-type="bestMatchType"
            />
        </BaseSection>

        <BaseSection
            v-if="mixes"
            :show-all-link="{ name: 'search/results/mixes', params: { keyword } }"
            title="Mixes"
        >
            <MixGridBlock
                :mixes="mixes"
                scroll
                scroll-mobile-only
                padded
            />
        </BaseSection>

        <BaseSection
            v-if="genres"
            :show-all-link="{ name: 'search/results/genres', params: { keyword } }"
            title="Genres"
        >
            <GenresList
                :genres="genres"
                hor-scroll
                hor-scroll-mobile
                padded
            />
        </BaseSection>

        <BaseSection
            v-if="artists"
            :show-all-link="{ name: 'search/results/artists', params: { keyword } }"
            title="Artists"
        >
            <ArtistBlock
                :artists="artists"
                overflowed-scrolling
                wrapping
            />
        </BaseSection>

        <BaseSection
            v-if="shows"
            :show-all-link="{ name: 'search/results/shows', params: { keyword } }"
            title="Shows"
        >
            <ShowGridBlock
                :shows="shows"
                scroll
                scroll-mobile-only
                padded
            />
        </BaseSection>

        <BaseSection
            v-if="mixes && mixes.length"
            :show-all-link="{ name: 'search/results/tracklist', params: { keyword } }"
            title="Tracklist"
            padded
        >
            <MixTableBlock
                :mixes="mixes"
                genres
                is-indexed-list
            />
        </BaseSection>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.best-match {
    text-align: center;

    @include desktop {
        text-align: unset;
    }
}

.title {
    @include desktop {
        margin: 0 0 $s-xl $size-grid-padding * 2;
    }

    @include h5;
}
</style>
